/**
 * ChunkedBatchAction — reusable long-running batch operation widget.
 *
 * For any ops button that needs to process N items one at a time to avoid
 * the ingress proxy timeout (~60s on Emergent k8s). Polls a `/one` endpoint
 * in a tight client-side loop and shows live progress.
 *
 * Backend contract:
 *   The endpoint must accept POST with no body and return JSON shaped:
 *     {
 *       status: 'ok' | 'queue_empty' | 'error',
 *       processed: { id, name, status: 'generated'|'regenerated'|'failed', ... } | null,
 *       remaining: number,
 *       remaining_before?: number   // for stuck-queue detection
 *     }
 *   When status === 'queue_empty', the loop stops.
 *
 * Usage:
 *   <ChunkedBatchAction
 *     testid="cleanup-legacy-photos"
 *     icon={Wand2}
 *     color="#FF3D8E"
 *     title="REGENERATE LEGACY PHOTOS"
 *     description="..."
 *     endpoint="/api/admin/cleanup/regenerate-legacy-photos/one"
 *     actionLabel="REGENERATE"
 *   />
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export function ChunkedBatchAction({
  testid,
  icon: Icon,
  color,
  title,
  description,
  endpoint,
  actionLabel,
  // Per-iteration timeout. Defaults to 60s which fits AI-gen workloads.
  perCallTimeoutMs = 60_000,
}) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, failed: 0, remaining: null, current: null });
  const [last, setLast] = useState(null);
  const stopRef = useRef(false);

  const run = async () => {
    setRunning(true);
    stopRef.current = false;
    setLast(null);
    setProgress({ done: 0, failed: 0, remaining: null, current: null });
    let done = 0;
    let failed = 0;
    let lastRemaining = null;

    while (!stopRef.current) {
      let resp;
      try {
        resp = await axios.post(
          `${API_URL}${endpoint}`,
          {},
          { withCredentials: true, timeout: perCallTimeoutMs }
        );
      } catch (e) {
        toast.error(`Stopped: ${e.response?.data?.detail || e.message}`);
        break;
      }
      const data = resp.data || {};
      if (data.status === 'queue_empty') {
        lastRemaining = 0;
        break;
      }
      if (data.processed) {
        const s = data.processed.status;
        if (s === 'failed') failed += 1;
        else done += 1;
      }
      lastRemaining = data.remaining ?? lastRemaining;
      setProgress({
        done,
        failed,
        remaining: lastRemaining,
        current: data.processed?.name || null,
      });
      // Anti-stuck: if the same failure keeps the queue at the same size, bail.
      if (data.processed?.status === 'failed' && lastRemaining === (data.remaining_before ?? lastRemaining)) {
        toast.warning('Last item failed and queue did not advance — stopping');
        break;
      }
    }

    const summary = `${done} done · ${failed} failed · ${lastRemaining ?? 0} remaining`;
    if (failed === 0 && done > 0) toast.success(summary);
    else if (done > 0) toast.warning(summary);
    setLast(summary);
    setRunning(false);
    stopRef.current = false;
  };

  const stop = () => { stopRef.current = true; };

  return (
    <div className="border-t border-[#2A0F3D] pt-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <p className="font-mono text-sm font-bold flex items-center gap-2" style={{ color }}>
            {Icon && <Icon className="w-4 h-4" />} {title}
          </p>
          <p className="text-[#6B8E73] font-mono text-xs mt-1">{description}</p>
        </div>
        {running ? (
          <Button
            onClick={stop}
            className="bg-[#FF3B30] text-white hover:bg-[#FF5040] rounded-none font-mono"
            data-testid={`${testid}-stop-btn`}
          >
            STOP
          </Button>
        ) : (
          <Button
            onClick={run}
            className="rounded-none font-mono"
            style={{ background: color, color: '#050003' }}
            data-testid={`${testid}-btn`}
          >
            {actionLabel}
          </Button>
        )}
      </div>
      {running && (
        <div
          className="mt-2 p-2 text-[#A3C7A8] font-mono text-xs"
          style={{ background: `${color}10`, border: `1px solid ${color}` }}
          data-testid={`${testid}-progress`}
        >
          // {progress.done} done · {progress.failed} failed · {progress.remaining ?? '?'} remaining
          {progress.current && (
            <div className="text-[#6B8E73]">{`> working on: ${progress.current}`}</div>
          )}
        </div>
      )}
      {last && !running && (
        <div
          className="mt-2 bg-[rgba(57,255,20,0.06)] border border-[#39FF14] p-2 text-[#A3C7A8] font-mono text-xs"
          data-testid={`${testid}-result`}
        >
          // {last}
        </div>
      )}
    </div>
  );
}
